import {
    ChainModifiers,
    EntriesQueries,
    Entry,
    EntryCollection,
    EntrySkeletonType,
} from 'contentful';
import { useEffect, useState } from 'react';

import { APIResponse } from '../models';
import { contentfulApi } from '../utils/contentful';

import { QuickTip, QuickTipImage } from './models/Dashboard';
import { CoordinatorMessageTemplate } from './models/CoordinatorMessageTemplate';
import { InternalLink } from './models/InternalLink';
import { ExternalLink } from './../store/models/ExternalLink';

export interface ContentTypeMap {
    coordinatorMessageTemplate: CoordinatorMessageTemplate;
    quickTip: QuickTip;
    quickTipImage: QuickTipImage;
    internalLink: InternalLink;
    externalLink: ExternalLink;
}

type QueryType<T extends EntrySkeletonType['fields']> = EntriesQueries<
    EntrySkeletonType<T>,
    ChainModifiers
>;

export const isContentType = <K extends keyof ContentTypeMap>(
    key: K,
    entry: Entry<unknown>
): entry is Entry<ContentTypeMap[K]> => entry.sys.contentType.sys.id === key;

export const loadFromContentful = async <T extends EntrySkeletonType>(
    contentType: string,
    query?: QueryType<T>
): Promise<APIResponse<EntryCollection<T>>> => {
    const _query = query || {};
    const result = await contentfulApi.getEntries<T, unknown>({
        content_type: contentType, // eslint-disable-line camelcase
        include: 10,
        ..._query,
    });

    return result;
};

export const useFromContentful = <T extends EntrySkeletonType['fields']>(
    contentType: string,
    query?: QueryType<EntrySkeletonType<T>>
) => {
    const [content, setContent] = useState<EntryCollection<EntrySkeletonType<T>> | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            try {
                setIsLoading(true);
                const response = await loadFromContentful<EntrySkeletonType<T>>(contentType, query);

                if (response && response.data) {
                    setContent(response.data);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetch();
    }, [contentType]);

    return { content, isLoading };
};
